import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  VStack,
  type StackProps,
} from '@frontend/components';

import {
  AXES_DEFAULTS,
  failedColorHex,
  temporarilySimplifiedColorHex,
  tooComplexColorHex,
} from '../constants';
import { FailedIcon, ViewProblemIcon } from './Icons';
import {
  failedLanguage,
  temporarilySimplifiedLanguage,
  tooComplexLanguage,
} from './Language';

export type ViewerAlertsData = {
  temporarilySimplified?: Set<string>; //bodyIds that are temporarily simplified
  tooComplex?: Set<string>; //bodyIds that are too complex to view
  failed?: Set<string>; //partIds that failed ingestion to paperless parts
  numBodies: number;
  numParts: number;
} & StackProps;

//In the future we need to improve html/css for locating the cube, and CADViewerInfo
//For now, these alerts are placed with this wrapper
const leftOffset = AXES_DEFAULTS.AXES_SIZE; //cube width
const rightOffset = '200px'; //This is width of `<CADViewerInfo>` content

export const ViewerAlerts = ({
  temporarilySimplified,
  tooComplex,
  failed,
  numBodies,
  numParts,
}: ViewerAlertsData) => {
  if (!temporarilySimplified?.size && !tooComplex?.size && !failed?.size) {
    return null;
  }

  return (
    //eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    <VStack
      position="absolute"
      bottom="0px"
      left={leftOffset}
      right={rightOffset}
      zIndex={1}
      p={2}
      overflow="hidden"
      flex="0 0 auto"
    >
      <VStack align="stretch">
        {temporarilySimplified?.size ? (
          <Alert
            status="info"
            shadow="popover"
            borderRadius="md"
            alignItems="flex-start"
          >
            <ViewProblemIcon color={temporarilySimplifiedColorHex} size={3} />
            <Box display="flex" flexDirection="column" pl={1}>
              <AlertTitle>
                {temporarilySimplifiedLanguage.headlineForBodies(
                  temporarilySimplified.size,
                  numBodies,
                )}
              </AlertTitle>
              <AlertDescription>
                {temporarilySimplifiedLanguage.descriptionForBodies}
                <Button
                  size="md"
                  ml={1}
                  colorScheme="blue"
                  variant="link"
                  as="a"
                  href={temporarilySimplifiedLanguage.helpUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  display="inline"
                >
                  Learn more
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}

        {tooComplex?.size ? (
          <Alert
            status="warning"
            shadow="popover"
            borderRadius="md"
            alignItems="flex-start"
          >
            <ViewProblemIcon color={tooComplexColorHex} size={3} />
            <Box display="flex" flexDirection="column" pl={1}>
              <AlertTitle>
                {tooComplexLanguage.headlineForBodies(
                  tooComplex.size,
                  numBodies,
                )}
              </AlertTitle>
              <AlertDescription>
                {tooComplexLanguage.descriptionForBodies(numBodies)}
                <Button
                  size="md"
                  ml={1}
                  colorScheme="blue"
                  variant="link"
                  as="a"
                  href={tooComplexLanguage.helpUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  display="inline"
                >
                  Learn more
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}

        {failed?.size ? (
          <Alert
            status="error"
            shadow="popover"
            borderRadius="md"
            alignItems="flex-start"
          >
            <FailedIcon color={failedColorHex} size={3} />
            <Box display="flex" flexDirection="column" pl={1}>
              <AlertTitle>
                {failedLanguage.headlineForBodies(failed.size, numParts)}
              </AlertTitle>
              <AlertDescription>
                {failedLanguage.descriptionForBodies(numParts)}
                {/* <br />({formatConjunction([...failed])}) */}
                <Button
                  size="md"
                  ml={1}
                  colorScheme="blue"
                  variant="link"
                  as="a"
                  href={failedLanguage.helpUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  display="inline"
                >
                  Learn more
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}
      </VStack>
    </VStack>
  );
};
